<!-- eslint-disable unicorn/prefer-dom-node-text-content -->
<script lang="ts" setup>
const props = defineProps<{
  tag?: keyof HTMLElementTagNameMap
  disabled?: boolean
  placeholder?: string
  inputClass?: string
  maxlength?: number
  autofocus?: boolean
  onUpdate?: (value: string) => void
  onBlur?: () => void
}>()

const element = ref<HTMLElement>()
const modelValue = defineModel<string>()

// Methods
function focus() {
  getRef(({ node }) => {
    node.focus()

    const selection = window.getSelection()
    selection?.selectAllChildren(node)
    selection?.collapseToEnd()
  })
}

function getRef(callback: (params: { node: HTMLElement, text: string, setText: (text: string) => void }) => void) {
  const node = element.value

  if (!node)
    return

  // ? Remove single <br> element to show placeholder
  if (node.innerHTML === '<br>')
    node.innerHTML = ''

  const text = node.innerText || ''
  const setText = (content: string) => node.innerText = content

  callback({ node, text, setText })
}

function onKeyPress(event: KeyboardEvent) {
  getRef(({ text }) => {
    const { maxlength } = props

    if (maxlength && text.length > maxlength)
      event.preventDefault()
  })
}

function update() {
  getRef(({ text }) => {
    modelValue.value = text
    props.onUpdate?.(text)
  })
}

// Watch for outside modeValue changes
watchEffect(() =>
  getRef(({ node, text, setText }) => {
    const newValue = modelValue.value

    if (newValue !== text)
      setText(newValue ?? '')

    if (props.placeholder)
      node.setAttribute('placeholder', props.placeholder)
  })
)

onMounted(() => {
  if (props.autofocus)
    focus()
})
</script>

<template>
  <component :is="tag || 'div'" :class="{ 'opacity-60': disabled, 'gap-2 flex items-center': $slots.default }">
    <span
      ref="element"
      v-memo="[disabled, placeholder, inputClass]"
      tabindex="0"
      class="cursor-text break-all empty:before:content-[attr(placeholder)] before:opacity-60 before:cursor-text"
      :class="inputClass"
      :placeholder
      :contenteditable="!disabled"
      @keypress="onKeyPress"
      @input="update"
      @blur="onBlur"
      v-text="modelValue"
    />

    <button v-if="$slots.default" @click="focus">
      <slot />
    </button>

    <slot name="trailing" />
  </component>
</template>
